import type {
  ConversationEvent,
  ConversationEventWithUuid,
  ConversationSource,
  FSSActionEvent,
} from "@mirage/service-fss-bridge/service/bridge_types";
import type { ActionSource } from "@mirage/service-fss-bridge/service/types";
import { tagged } from "@mirage/service-logging";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import { useEffect, useState } from "react";
import { observeActionEvents, observeConversationEvents } from "..";

const logger = tagged("[fss] bridgeObservers");

// Automatically maps each conversation source to its corresponding event type
type ConversationEventMap = {
  [K in ConversationSource]: Extract<ConversationEvent, { source: K }>;
};

// Utility type to get the event type for a specific ConversationSource
type ConversationEventForSource<T extends ConversationSource> =
  T extends keyof ConversationEventMap
    ? ConversationEventMap[T]
    : ConversationEvent;

// Utility type to get the union of event types for an array of ConversationSources
type ConversationEventsForSources<T extends readonly ConversationSource[]> =
  T extends readonly (infer U)[]
    ? U extends ConversationSource
      ? ConversationEventForSource<U>
      : never
    : never;

/**
 * This hook is used to observe ConversationEvents from the FSS bridge, and will return
 * only the latest event that matches the sources passed into the hook.
 *
 * The hook is typed to return the correct ConversationEvent type for the given source.
 *
 * ```typescript
 * // example usage for single source
 * const conversationEvent = useConversationEvents("fss-file");
 *
 * // if you pass in an array of sources, the hook will return the latest event that matches the sources
 * const conversationEvent = useConversationEvents(["fss-file", "fss-folder"]);
 *
 * // you can also pass in no sources to get the latest event, period.
 * const conversationEvent = useConversationEvents();
 * ```
 */
export function useConversationEvents<T extends readonly ConversationSource[]>(
  events: T,
): ConversationEventsForSources<T> | undefined;
export function useConversationEvents<T extends ConversationSource>(
  events: T,
): ConversationEventForSource<T> | undefined;
export function useConversationEvents(): ConversationEventWithUuid | undefined;
export function useConversationEvents(
  events?: ConversationSource | ConversationSource[],
): ConversationEventWithUuid | undefined {
  const [conversationEvent, setConversationEvent] = useState<
    ConversationEventWithUuid | undefined
  >(undefined);

  useEffect(() => {
    if (!isEmbedPage()) return;

    const subscription = observeConversationEvents().subscribe((event) => {
      if (event) {
        if (!events) {
          logger.debug("Setting conversation event: ", event);
          setConversationEvent(event);
          return;
        }
        if (
          Array.isArray(events)
            ? events.includes(event.source as ConversationSource)
            : events === event.source
        ) {
          logger.debug("Setting conversation event: ", event);
          setConversationEvent(event);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [events]);

  return conversationEvent;
}

// Automatic type mapping - extracts all source types from FSSActionEvent
type FSSActionSources = FSSActionEvent["source"];

// Automatically maps each FSS action source to its corresponding event type
type ActionEventMap = {
  [K in FSSActionSources]: Extract<FSSActionEvent, { source: K }>;
};

// Utility type to get the event type for a specific ActionSource
type ActionEventForSource<T extends ActionSource> =
  T extends keyof ActionEventMap ? ActionEventMap[T] : FSSActionEvent;

// Utility type to get the union of event types for an array of ActionSources
type ActionEventsForSources<T extends readonly ActionSource[]> =
  T extends readonly (infer U)[]
    ? U extends ActionSource
      ? ActionEventForSource<U>
      : never
    : never;

/**
 * This hook is used to observe FSSActionEvents from the FSS bridge, and will return
 * only the latest event that matches the sources passed into the hook.
 *
 * The hook is typed to return the correct FSSActionEvent type for the given source.
 *
 * ```typescript
 * // example usage for single source
 * const actionEvent = useActionEvents("update.theme");
 *
 * // if you pass in an array of sources, the hook will return the latest event that matches the sources
 * const actionEvent = useActionEvents(["update.theme", "add.source"]);
 *
 * // you can also pass in no sources to get the latest event, period.
 * const actionEvent = useActionEvents();
 * ```
 */
export function useActionEvents<T extends readonly ActionSource[]>(
  events: T,
): ActionEventsForSources<T> | undefined;
export function useActionEvents<T extends ActionSource>(
  events: T,
): ActionEventForSource<T> | undefined;
export function useActionEvents(): FSSActionEvent | undefined;
export function useActionEvents(
  events?: ActionSource | ActionSource[],
): FSSActionEvent | undefined {
  const [actionEvent, setActionEvent] = useState<FSSActionEvent | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!isEmbedPage()) return;

    const subscription = observeActionEvents().subscribe((event) => {
      if (!events) {
        logger.info("Setting action event: ", event);
        setActionEvent(event);
        return;
      }
      if (
        Array.isArray(events)
          ? events.includes(event?.source as ActionSource)
          : events === event?.source
      ) {
        logger.info("Setting action event: ", event);
        setActionEvent(event);
      }
    });
    return () => subscription.unsubscribe();
  }, [events]);

  return actionEvent;
}
